import { apiClient } from "./client";

export interface DictationSegment {
  startSeconds: number;
  endSeconds: number;
  text: string;
}

export interface DictationTranscript {
  text: string;
  language: string;
  languageProbability: number;
  durationSeconds: number;
  provider: string;
  model: string;
  segments: DictationSegment[];
}

export interface DictationSoap {
  subjective: string;
  objective: string;
  assessment: string;
  plan: string;
  model: string;
}

export interface DictationResponse {
  encounterId: string;
  audioId: string;
  transcript: DictationTranscript;
  soap: DictationSoap;
}

/**
 * Upload an audio recording of an encounter — server runs STT (PhoWhisper
 * with OpenAI fallback) and returns a Vietnamese SOAP draft for the doctor
 * to review before saving. The browser must capture explicit PDPD consent
 * before calling.
 */
export const dictationApi = {
  async submit(
    encounterId: string,
    audio: Blob,
    opts: {
      consentGiven: boolean;
      chiefComplaint?: string | null;
      patientAgeYears?: number | null;
      patientSexLabel?: string | null;
      initialPrompt?: string | null;
      filename?: string;
    }
  ): Promise<DictationResponse> {
    const fd = new FormData();
    fd.append("audio", audio, opts.filename ?? "encounter.webm");
    fd.append("consentGiven", opts.consentGiven ? "true" : "false");
    if (opts.chiefComplaint) fd.append("chiefComplaint", opts.chiefComplaint);
    if (opts.patientAgeYears != null)
      fd.append("patientAgeYears", String(opts.patientAgeYears));
    if (opts.patientSexLabel) fd.append("patientSexLabel", opts.patientSexLabel);
    if (opts.initialPrompt) fd.append("initialPrompt", opts.initialPrompt);

    const res = await apiClient.post<DictationResponse>(
      `/v1/encounters/${encounterId}/dictation`,
      fd,
      {
        // STT is synchronous; long encounters can take 30–60s on CPU.
        timeout: 180_000,
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    return res.data;
  },
};
